import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import { PaginationComponent } from '../../../../components/inputs/pagination/pagination.component';
import { DataTableComponent } from '../../../../components/ui/data-table/data-table.component';
import { EmptyStateComponent } from '../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import {
  DeliveryReportService,
  FAILURE_EXPORT_CAP,
  ReceiptRow,
} from '../../../../services/reports/delivery-report.service';
import {
  FAILED_RECEIPT_STATES,
  failureReason,
  receiptStatus,
} from '../../../../services/reports/report-format';
import {
  DEFAULT_REPORT_PERIOD,
  ReportPeriodDays,
  periodPhrase,
} from '../../../../services/reports/report-period';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';

/** One failed receipt, ready for the table. */
interface FailureView {
  key: string | number;
  name: string;
  number: string;
  subject: string;
  messageUuid: string;
  statusLabel: string;
  statusTone: string;
  reason: string;
  country: string;
  when: string;
  exact: string;
}

const exactFormat = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

function exactTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : exactFormat.format(date);
}

function toView(row: ReceiptRow, index: number): FailureView {
  const status = receiptStatus(row.deliveryStatus);
  const number = String(row.mobileNumber ?? '').trim();
  return {
    key: row.uuid ?? row.id ?? `row-${index}`,
    name: String(row.fullName ?? '').trim(),
    number,
    subject: String(row.subject ?? '').trim() || 'Untitled message',
    messageUuid: String(row.messageUuid ?? ''),
    statusLabel: status.label,
    statusTone: status.tone,
    reason: failureReason(row.deliveryStatus),
    country: String(row.country ?? '').trim(),
    when: formatWhen(row.createdAt),
    exact: exactTime(row.createdAt),
  };
}

/**
 * Who did not get the SMS: every failed delivery receipt in the period,
 * newest first, across all sends. Search by number or name, narrow to one
 * failure state, open the send's report, or download the numbers.
 */
@Component({
  selector: 'failures-report',
  standalone: true,
  imports: [
    MatTooltipModule,
    SearchInputComponent,
    StatusFilterComponent,
    PaginationComponent,
    DataTableComponent,
    EmptyStateComponent,
    StatusComponent,
  ],
  templateUrl: './failures-report.component.html',
  styleUrls: ['../report-table.css', './failures-report.component.css'],
})
export class FailuresReportComponent implements OnInit, OnChanges {
  private reports = inject(DeliveryReportService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  /** Reporting period in days. */
  @Input() days: ReportPeriodDays = DEFAULT_REPORT_PERIOD;
  /** Bumped by the page's Refresh button. */
  @Input() reloadKey = 0;

  readonly formatCount = formatCount;

  readonly statusOptions: StatusFilterOption[] = [
    { value: null, title: 'All failures', icon: 'list', tone: 'neutral' },
    ...FAILED_RECEIPT_STATES.map((state) => ({
      value: state,
      title: receiptStatus(state).label,
      icon: 'error',
      tone: 'danger' as const,
    })),
  ];

  term = '';
  state: string | null = null;
  page = 0;
  size = 20;

  rows: FailureView[] = [];
  total = 0;
  totalPages = 1;
  status: 'loading' | 'ready' | 'error' = 'loading';

  exporting = false;
  notice = '';
  noticeTone: 'ok' | 'bad' = 'ok';
  copiedKey: string | number | null = null;
  private copiedTimer?: ReturnType<typeof setTimeout>;
  /** The running "Download failed numbers" export, if any. */
  private exportSub?: Subscription;

  private load$ = new Subject<void>();
  private search$ = new Subject<string>();

  get periodText(): string {
    return periodPhrase(this.days);
  }

  get filtered(): boolean {
    return this.state !== null;
  }

  get emptyLine(): string {
    return `No failed deliveries in ${this.periodText}.`;
  }

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => {
        if (term !== this.term) this.cancelExport();
        this.term = term;
        this.page = 0;
        this.load$.next();
      });

    this.load$
      .pipe(
        switchMap(() => {
          this.status = 'loading';
          return this.reports
            .failures({ days: this.days, states: this.state ? [this.state] : [], term: this.term, page: this.page, size: this.size })
            .pipe(
              map((result) => ({ ...result, failed: false })),
              catchError(() => of({ rows: [] as ReceiptRow[], total: 0, totalPages: 1, failed: true }))
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rows, total, totalPages, failed }) => {
        this.rows = rows.map(toView);
        this.total = total;
        this.totalPages = totalPages;
        this.status = failed ? 'error' : 'ready';
      });

    this.load$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const days = changes['days'];
    const reload = changes['reloadKey'];
    if ((days && !days.firstChange) || (reload && !reload.firstChange)) {
      this.page = 0;
      this.notice = '';
      if (days && !days.firstChange) this.cancelExport();
      this.load$.next();
    }
  }

  reload(): void {
    this.load$.next();
  }

  onSearch(term: string): void {
    this.search$.next(term.trim());
  }

  onStateChange(value: string | null): void {
    if (value !== this.state) this.cancelExport();
    this.state = value;
    this.page = 0;
    this.load$.next();
  }

  clearFilters(): void {
    this.onStateChange(null);
  }

  onPage(page: number): void {
    this.page = Number(page) || 0;
    this.load$.next();
  }

  /** The pager follows a size change with changePage(0), which reloads. */
  onPageSize(size: number): void {
    this.size = Number(size) || 20;
    this.page = 0;
  }

  open(row: FailureView): void {
    if (!row.messageUuid) return;
    this.router.navigate(['/organization/messages/report', row.messageUuid], {
      queryParams: { status: 'failed' },
    });
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  openFromKeyboard(event: Event, row: FailureView): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.open(row);
  }

  copyNumber(row: FailureView): void {
    if (!row.number || typeof navigator === 'undefined' || !navigator.clipboard) {
      this.say('Copying is not available in this browser.', 'bad');
      return;
    }
    navigator.clipboard.writeText(row.number).then(
      () => {
        this.copiedKey = row.key;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => (this.copiedKey = null), 1600);
      },
      () => this.say('The number could not be copied. Select it and copy it instead.', 'bad')
    );
  }

  /** Every failed number matching the search and filter, as a CSV file. */
  downloadCsv(): void {
    if (this.exporting) return;
    this.exporting = true;
    this.notice = '';
    // The file is named after what was asked for, not what is on screen when it finishes.
    const { days, state, term } = this;
    this.exportSub = this.reports
      .allFailures({ days, states: state ? [state] : [], term })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.exporting = false;
          this.exportSub = undefined;
          if (!rows.length) {
            this.say('There are no failed numbers to download for this period and filter.', 'ok');
            return;
          }
          const csv = toCsv(rows, [
            { label: 'Name', value: (row) => row.fullName ?? '' },
            { label: 'Mobile number', value: (row) => row.mobileNumber ?? '' },
            { label: 'Country', value: (row) => row.country ?? '' },
            { label: 'Message', value: (row) => row.subject ?? '' },
            { label: 'Status', value: (row) => receiptStatus(row.deliveryStatus).label },
            { label: 'Reason', value: (row) => failureReason(row.deliveryStatus) },
            { label: 'Failed at', value: (row) => row.createdAt ?? '' },
            { label: 'Message ID', value: (row) => row.messageUuid ?? '' },
          ]);
          downloadCsv(
            csvFileName('failed-numbers', `last-${days}-days`, state ?? '', term),
            csv
          );
          const capped = rows.length >= FAILURE_EXPORT_CAP;
          this.say(
            capped
              ? `Downloaded the newest ${formatCount(rows.length)} failed numbers. Narrow the period or filter to get the rest.`
              : `Downloaded ${formatCount(rows.length)} failed ${rows.length === 1 ? 'number' : 'numbers'}.`,
            'ok'
          );
        },
        error: () => {
          this.exporting = false;
          this.exportSub = undefined;
          this.say('The download did not finish. Check your connection, then try again.', 'bad');
        },
      });
  }

  /** A running export stops when the period, filter or search changes under it. */
  private cancelExport(): void {
    if (!this.exporting || !this.exportSub) return;
    this.exportSub.unsubscribe();
    this.exportSub = undefined;
    this.exporting = false;
    this.say('The download stopped because the period, filter or search changed. Download again for the new list.', 'ok');
  }

  private say(message: string, tone: 'ok' | 'bad'): void {
    this.notice = message;
    this.noticeTone = tone;
  }
}
